import { getLanguage } from '@dynatrace-sdk/user-preferences';

/**
 * Properties describing how a piece of text is formatted.
 * @internal
 */
export type Font = {
  fontStyle?: string;
  fontWeight?: string;
  fontSize: string;
  fontFamily: string;
};

// Create a singleton for the segmenter that is only initialized when it is actually
// needed at runtime. Otherwise a simple import to the typography package
// will yield in a network request for getLanguage(). In the shell itself, it might
// not even be available yet.
let localizedSegmenter: Intl.Segmenter;
function getLocalizedSegmenter(): Intl.Segmenter {
  if (localizedSegmenter) {
    return localizedSegmenter;
  }

  const segmenter = new Intl.Segmenter(getLanguage(), {
    granularity: 'grapheme',
  });
  localizedSegmenter = segmenter;

  return localizedSegmenter;
}

/**
 * The Unicode character representing an ellipsis.
 * @internal
 */
export const ELLIPSIS_CHARACTER = '\u2026';

/**
 * Max iterations for finding the perfect truncated text for a given width
 * @internal
 */
export const ELLIPSIS_APPROXIMATION_THRESHOLD = 15;

let inMemContext2d: CanvasRenderingContext2D;

/**
 * Calculates the width of formatted text in pixels.
 *
 * @param text - The text to calculate the width of
 * @param textFont - The font settings for the text
 * @internal
 */
export function getTextWidth(text: string, textFont: Font): number {
  const context = getCanvasContext2d();

  context.font = getFontString(textFont);

  const textMetrics = context.measureText(text);

  // this branch is relevant for mocked canvas API in unit tests
  if (textMetrics.width < 0) {
    return textMetrics.width;
  }

  // Development note: On macOS Chrome and macOS Firefox, emojis passed to the
  // text measurement return a faulty measurement. This is most likely related to
  // this chromium bug https://issues.chromium.org/issues/40461560, which is at the time of writing
  // still open. If a text only consists of emojis, the measurement will be fairly off, which will
  // in turn break the average character width calculation following afterwards.
  // In most cases we anticipate that other letter characters measurements will smooth out the average calculation.

  // more accurate than using metrics width
  // see https://developer.mozilla.org/en-US/docs/Web/API/TextMetrics#measuring_text_width
  const actualTextWidth =
    Math.abs(textMetrics.actualBoundingBoxLeft) +
    Math.abs(textMetrics.actualBoundingBoxRight);

  // Scrap sub-pixel accuracy and always round up to be on the safer side
  return Math.ceil(actualTextWidth);
}

/**
 * Calculates the height of formatted text in pixels.
 * @param text - The text to calculate the height of
 * @param textFont - The font settings for the text
 * @internal
 */
export function getTextHeight(text: string, textFont: Font): number {
  const context = getCanvasContext2d();

  context.font = getFontString(textFont);

  const textMetrics = context.measureText(text);

  const actualTextHeight =
    Math.abs(textMetrics.actualBoundingBoxAscent) +
    Math.abs(textMetrics.actualBoundingBoxDescent);

  // Scrap sub-pixel accuracy and always round up to be on the safer side
  return Math.ceil(actualTextHeight);
}

/**
 * Cuts off text that exceeds the maximum width in the center and
 * concatenates both shortened ends of the text with an ellipsis.
 *
 * @param originalText - The text to be truncated if its width exceeds <code>maxWidthInPx</code>
 * @param textFont - Different font settings to be considered when determining the width of the text
 * @param maxWidthInPx - If the text's width exceeds this value, the text will be truncated to a string whose width is guaranteed to be below it
 * @internal
 */
export function centerEllipsizeText(
  originalText: string,
  textFont: Font,
  maxWidthInPx: number,
): string {
  const ellipsisInfo = calculateEllipsisInfo(
    originalText,
    textFont,
    maxWidthInPx,
  );

  if (ellipsisInfo === null) {
    return originalText;
  }

  const { ellipsisWidth, avgLetterWidth } = ellipsisInfo;
  const availableTextWidth = maxWidthInPx - ellipsisWidth;

  if (availableTextWidth <= ellipsisWidth) {
    return ELLIPSIS_CHARACTER;
  }

  const initialPrefixSuffixLength =
    Math.floor(availableTextWidth / avgLetterWidth / 2) + 1;

  let prefix: string;
  let suffix: string;
  let ellipsizedText: string;
  let approximatedTextWidth: number;
  let attempt = 1;
  let prefixLength = initialPrefixSuffixLength;
  let suffixLength = initialPrefixSuffixLength;

  const segmenter = getLocalizedSegmenter();
  const originalSegmentedText = Array.from(segmenter.segment(originalText)).map(
    (segment) => segment.segment,
  );

  do {
    // Need to copy the original segmented array, because slice does mutate the array
    // it is called on, and that can lead to unwanted side effects when running this
    // loop more often.
    const originalSegmentedTextCopy = [...originalSegmentedText];
    prefixLength -= attempt % 2 === 1 ? 1 : 0;
    suffixLength -= attempt % 2 === 0 ? 1 : 0;

    prefix = originalSegmentedTextCopy.slice(0, prefixLength).join('');
    suffix = originalSegmentedTextCopy.slice(suffixLength * -1).join('');

    ellipsizedText = `${prefix}${ELLIPSIS_CHARACTER}${suffix}`;
    approximatedTextWidth = getTextWidth(ellipsizedText, textFont);

    attempt++;
  } while (
    attempt <= ELLIPSIS_APPROXIMATION_THRESHOLD &&
    approximatedTextWidth >= maxWidthInPx
  );

  return ellipsizedText;
}

/**
 * Transforms a font settings object into a CSS font specifier string
 * that can be used on 2D canvas contexts.
 */
function getFontString(textFont: Font): string {
  const { fontStyle, fontWeight, fontSize, fontFamily } = textFont;

  // Note: always drop default values for fontStyle and fontWeight as
  // these can randomly (!) break text width detection!
  return [
    fontStyle === 'normal' ? '' : fontStyle,
    fontWeight === '400' ? '' : fontWeight,
    fontSize,
    fontFamily,
  ]
    .filter((value) => value !== undefined && value !== '')
    .map((value) =>
      // we need to resolve CSS variables manually because they are not supported by the canvas API
      value?.trim().startsWith('var(')
        ? value.replace(/^\s*var\(--[0-9A-Za-z-]+,\s*([^)]+)\)\s*$/, '$1')
        : value,
    )
    .join(' ');
}

/**
 * Returns an object containing the width of a given formatted text and the
 * width of an ellipsis character with the same text format or `null`
 * if the text width does not exceed its maximum available width.
 * @internal
 */
export function calculateEllipsisInfo(
  originalText: string,
  textFont: Font,
  maxWidthInPx: number,
): { ellipsisWidth: number; avgLetterWidth: number } | null {
  const segmenter = getLocalizedSegmenter();
  const originalTextLength = Array.from(segmenter.segment(originalText)).length;

  if (originalTextLength <= 1) {
    return null;
  }

  const textWidth = getTextWidth(originalText, textFont);
  const avgLetterWidth = textWidth / originalTextLength;

  return textWidth > maxWidthInPx
    ? {
        ellipsisWidth: getTextWidth(ELLIPSIS_CHARACTER, textFont),
        avgLetterWidth,
      }
    : null;
}

/** Returns the canvas 2D context used for measuring the width of formatted text. */
function getCanvasContext2d() {
  if (!inMemContext2d) {
    const canvas = document.createElement('canvas');
    canvas.hidden = true;
    const context = canvas.getContext('2d', { alpha: false });

    if (context !== null) {
      context.imageSmoothingEnabled = false;
      context.imageSmoothingQuality = 'low';
      inMemContext2d = context;
    } else {
      throw new Error('Failed to create a Canvas 2D context');
    }
  }

  return inMemContext2d;
}
